import { Alert, Button } from "@opowiedzo/ui";
import {
  useCustom,
  useCustomMutation,
  useGetIdentity,
  useInvalidate,
  useOne,
} from "@refinedev/core";
import { useState } from "react";
import { Link, useParams } from "react-router";

import { Badge } from "@/components/Badge";
import { Labeled, TextArea } from "@/components/Form";
import {
  BackLink,
  Card,
  CardNote,
  DefinitionList,
  DetailGrid,
  ErrorBox,
  Loading,
  PageHeader,
} from "@/components/Page";
import {
  failureKindLabel,
  formatCallOffset,
  formatDateTime,
  formatDuration,
  interviewRoleLabel,
  interviewStatus,
  voiceProviderLabel,
} from "@/lib/format";
import type { AdminIdentity } from "@/providers/authProvider";
import type { InterviewCallRow } from "./list";

/**
 * Statusy, z których backend nie pozwala ponowić (interviews_router.py).
 * "Odbyta" i "Odrzucone" są stanami końcowymi, a przy "Dzwonimy" i "Rozmowa
 * trwa" telefon właśnie dzwoni - klikalny przycisk kończyłby się 409.
 */
const NIE_PONAWIAMY = ["completed", "declined", "dialing", "in_progress"];

/**
 * Stany, z których pierwsze kliknięcie nie dzwoni, tylko pyta dostawcę
 * (`_failure_kind` w interviews_router.py). "Bez potwierdzenia" znaczy, że
 * zlecenie mogło dojść; "Zaplanowana" po terminie znaczy, że zadanie mogło
 * paść już po tym, jak dostawca je przyjął. W obu wypadkach telefon może
 * właśnie dzwonić u narratora.
 */
const NAJPIERW_PYTAMY = ["unknown", "scheduled"];

interface AttemptRow {
  attempt_number: number;
  started_at: string;
  finished_at: string | null;
  provider: string;
  provider_conversation_id: string | null;
  outcome: string | null;
  failure_kind: string | null;
  sip_status_code: number | null;
  failure_reason: string | null;
}

interface InterviewCallDetail extends InterviewCallRow {
  created_at: string;
  provider_call_id: string | null;
  agent_id: string | null;
  cost_credits: number | null;
  recording_id: string | null;
  attempts: AttemptRow[];
  transcript_lines: number;
}

interface RetryResult extends Record<string, unknown> {
  outcome: string;
  message: string;
}

export function InterviewCallShowPage() {
  const { id = "" } = useParams();
  const { query } = useOne<InterviewCallDetail>({ resource: "interview-calls", id });
  const { data: identity } = useGetIdentity<AdminIdentity | null>();

  const call = query.data?.data;

  if (query.isLoading) return <Loading />;
  if (query.error) return <ErrorBox error={query.error} />;
  if (!call) return null;

  const status = interviewStatus(call.status);

  return (
    <>
      <BackLink to="/rozmowy">Rozmowy telefoniczne</BackLink>
      <PageHeader
        title={`Rozmowa z ${formatDateTime(call.scheduled_at)}`}
        description={call.memoir_title ?? undefined}
        actions={<Badge tone={status.tone}>{status.label}</Badge>}
      />

      <DetailGrid
        main={
          <>
            <Card title="Pytanie">
              <p className="text-lg text-ink">
                {call.question ?? "Rozmowa poza pytaniem tygodnia - zlecona doraźnie."}
              </p>
            </Card>

            <Card title="Przebieg">
              <DefinitionList
                rows={[
                  ["Zaplanowana", formatDateTime(call.scheduled_at)],
                  ["Rozpoczęta", formatDateTime(call.started_at)],
                  ["Zakończona", formatDateTime(call.ended_at)],
                  ["Czas trwania", formatDuration(call.duration_s)],
                  ["Numer próby", String(call.attempt)],
                  ["Powód niepowodzenia", call.failure_reason ?? "-"],
                ]}
              />
            </Card>

            <AttemptsCard attempts={call.attempts} />

            <TranscriptPanel
              callId={call.id}
              lines={call.transcript_lines}
              contentAccess={identity?.content_access ?? false}
            />
          </>
        }
        side={
          <>
            <Card title="Skąd i dokąd">
              <DefinitionList
                rows={[
                  [
                    "Opowieść",
                    <Link
                      key="memoir"
                      to={`/opowiesci/${call.memoir_id}`}
                      className="text-accent underline underline-offset-4 hover:text-accent-dark"
                    >
                      {call.memoir_title ?? "Opowieść"}
                    </Link>,
                  ],
                  ["Narrator", call.storyteller_name ?? "-"],
                  [
                    "Wysyłka pytania",
                    call.prompt_delivery_id ? (
                      <Link
                        key="delivery"
                        to={`/wysylki-pytan/${call.prompt_delivery_id}`}
                        className="text-accent underline underline-offset-4 hover:text-accent-dark"
                      >
                        Zobacz wysyłkę
                      </Link>
                    ) : (
                      "brak - rozmowa doraźna"
                    ),
                  ],
                  [
                    "Historia",
                    call.story_id ? (
                      <Link
                        key="story"
                        to={`/historie/${call.story_id}`}
                        className="text-accent underline underline-offset-4 hover:text-accent-dark"
                      >
                        Powstała z tej rozmowy
                      </Link>
                    ) : (
                      "jeszcze nie powstała"
                    ),
                  ],
                ]}
              />
            </Card>

            <Card title="U dostawcy">
              <DefinitionList
                monoLabels
                rows={[
                  ["dostawca", voiceProviderLabel(call.provider)],
                  ["conversation_id", call.provider_conversation_id ?? "-"],
                  ["call_id", call.provider_call_id ?? "-"],
                  ["agent_id", call.agent_id ?? "-"],
                  ["zużycie", call.cost_credits === null ? "-" : String(call.cost_credits)],
                ]}
              />
              <CardNote>
                Zużycie jest w jednostkach rozliczeniowych dostawcy, nie w złotówkach - kursu nie
                znamy w chwili rozmowy.
              </CardNote>
            </Card>

            <RetryPanel
              callId={call.id}
              status={call.status}
              allowed={(identity?.permissions ?? []).includes("interviews.act")}
            />
          </>
        }
      />
    </>
  );
}

/** Oś prób: "18:00 zajęte, 18:15 nie odebrano, 19:00 rozmowa". */
function AttemptsCard({ attempts }: { attempts: AttemptRow[] }) {
  return (
    <Card title="Próby dodzwonienia się">
      {attempts.length === 0 ? (
        <p className="text-base text-ink-soft">
          Nie ma zapisanej ani jednej próby. Do dostawcy nic nie poszło.
        </p>
      ) : (
        <ol className="flex flex-col gap-3.25">
          {attempts.map((attempt) => {
            const wynik = interviewStatus(attempt.outcome);
            const udana = attempt.failure_kind === null;
            return (
              <li key={attempt.attempt_number} className="flex gap-2.5">
                <span
                  aria-hidden
                  className={`mt-1 size-2.5 shrink-0 rounded-pill ${
                    udana ? "bg-accent-alt" : "border border-line bg-bg"
                  }`}
                />
                <div>
                  <p className="text-base text-ink">
                    Próba {attempt.attempt_number}
                    <span className="text-ink-soft"> - {formatDateTime(attempt.started_at)}</span>
                  </p>
                  <p className="text-sm text-ink-soft">
                    {[
                      attempt.outcome ? wynik.label : "w toku",
                      attempt.failure_kind ? failureKindLabel(attempt.failure_kind) : null,
                      attempt.sip_status_code ? `SIP ${attempt.sip_status_code}` : null,
                      attempt.failure_reason,
                    ]
                      .filter(Boolean)
                      .join(" - ")}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      )}
      <CardNote>
        Kod SIP mówi więcej niż nasz status: 486 to zajęte, 480 niedostępny, 603 odrzucone przez
        człowieka. Przy diagnozie u operatora to pierwsza rzecz, o którą zapyta.
      </CardNote>
    </Card>
  );
}

interface Transcript extends Record<string, unknown> {
  summary: string | null;
  lines: { role: string; message: string | null; time_in_call_secs: number | null }[];
}

function TranscriptPanel({
  callId,
  lines,
  contentAccess,
}: {
  callId: string;
  lines: number;
  contentAccess: boolean;
}) {
  // Zapis pobieramy dopiero po kliknięciu. Każde takie żądanie zostawia wpis
  // w audycie, więc nie wolno go wysyłać "na wszelki wypadek" przy wejściu.
  const [pokaz, setPokaz] = useState(false);
  const { query } = useCustom<Transcript>({
    url: `/interview-calls/${callId}/transcript`,
    method: "get",
    queryOptions: { enabled: pokaz },
  });

  return (
    <Card title="Zapis rozmowy">
      {lines === 0 ? (
        <p className="text-base text-ink-soft">
          Z tej rozmowy nie ma zapisu. Albo się nie odbyła, albo dostawca jeszcze go nie oddał.
        </p>
      ) : !contentAccess ? (
        <p className="text-base text-ink-soft">
          Rozmowa ma {lines} wypowiedzi. Ich treść jest zamknięta - to cudze wspomnienie
          powiedziane na głos, a wgląd wymaga osobnego uprawnienia.
        </p>
      ) : !pokaz ? (
        <Button onClick={() => setPokaz(true)}>
          Pokaż zapis rozmowy ({lines}) - zostanie zapisane w audycie
        </Button>
      ) : query.isLoading ? (
        <Loading />
      ) : query.error ? (
        <ErrorBox error={query.error} />
      ) : (
        <div className="flex flex-col gap-3.5">
          {query.data?.data.summary && (
            <p className="text-base text-ink-soft italic">{query.data.data.summary}</p>
          )}
          {query.data?.data.lines.map((line, index) => (
            <article key={`${line.role}-${index}`}>
              <p className="text-sm text-ink-soft">
                {interviewRoleLabel(line.role)}
                {line.time_in_call_secs !== null && ` - ${formatCallOffset(line.time_in_call_secs)}`}
              </p>
              <p className="text-base whitespace-pre-line text-ink">{line.message ?? "-"}</p>
            </article>
          ))}
        </div>
      )}
    </Card>
  );
}

function RetryPanel({
  callId,
  status,
  allowed,
}: {
  callId: string;
  status: string;
  allowed: boolean;
}) {
  const invalidate = useInvalidate();
  const { mutate, mutation } = useCustomMutation<RetryResult>();
  const [powod, setPowod] = useState("");
  const [wynik, setWynik] = useState<string | null>(null);

  const zablokowane = NIE_PONAWIAMY.includes(status);
  const pytamy = NAJPIERW_PYTAMY.includes(status);

  if (!allowed) {
    return (
      <Card title="Ponowienie">
        <p className="text-base text-ink-soft">
          Twoja rola nie obejmuje dzwonienia. Drugi mail do narratora jest niezauważalny, drugi
          telefon już nie - tę decyzję podejmuje operations.
        </p>
      </Card>
    );
  }

  return (
    <Card title="Ponowienie">
      {pytamy ? (
        <p className="mb-3.5 text-base text-ink-soft">
          {status === "unknown"
            ? "Nie wiemy, czy ta rozmowa się odbyła: zlecenie poszło, a odpowiedź od dostawcy nie wróciła."
            : "Ta rozmowa nigdy nie ruszyła, ale zlecenie mogło dojść do dostawcy tuż przed awarią."}{" "}
          Ten przycisk NIE zadzwoni - najpierw zapyta dostawcę, czy zna tę rozmowę. Dopiero gdy jej
          nie zna, wiadomo, że nikt nie dzwonił, i kolejne kliknięcie zamówi połączenie.
        </p>
      ) : (
        <p className="mb-3.5 text-base text-ink-soft">
          Połączenie wraca do kolejki. Poza oknem godzinowym narratora poczeka - nie dzwonimy o
          szóstej rano.
        </p>
      )}

      {wynik && <Alert tone="info">{wynik}</Alert>}
      {mutation.error && <Alert tone="error">{mutation.error.message}</Alert>}

      <form
        className="mt-3.5 flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          mutate(
            {
              url: `/interview-calls/${callId}/retry`,
              method: "post",
              values: { reason: powod },
              successNotification: false,
              errorNotification: false,
            },
            {
              onSuccess: (odpowiedz) => {
                setPowod("");
                setWynik(odpowiedz.data.message);
                invalidate({
                  resource: "interview-calls",
                  invalidates: ["detail", "list"],
                  id: callId,
                });
              },
            },
          );
        }}
      >
        <Labeled label="Powód" hint="Trafia do rejestru audytu.">
          <TextArea
            required
            rows={3}
            value={powod}
            onChange={(event) => setPowod(event.target.value)}
            placeholder="Dlaczego dzwonimy jeszcze raz?"
          />
        </Labeled>
        <Button
          type="submit"
          variant="primary"
          disabled={mutation.isPending || zablokowane || powod.trim().length < 3}
        >
          {pytamy ? "Zapytaj dostawcę o tę rozmowę" : "Zadzwoń jeszcze raz"}
        </Button>
        {zablokowane && (
          <CardNote>
            Tej rozmowy nie da się ponowić: albo już się odbyła, albo telefon właśnie dzwoni.
          </CardNote>
        )}
      </form>
    </Card>
  );
}
